import React from 'react';
import { Smartphone, Library, Users, Swords, Shield, BarChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useI18n } from '../../contexts/I18nContext';

// Couleurs thématiques pour chaque fonctionnalité
const featureThemes = {
  collection: { 
    bgLight: 'bg-blue-50', 
    bgDark: 'bg-blue-100', 
    text: 'text-blue-600',
    iconBg: 'bg-blue-500',
    gradient: 'from-blue-500 to-blue-600'
  },
  deck: { 
    bgLight: 'bg-violet-50', 
    bgDark: 'bg-violet-100', 
    text: 'text-violet-600',
    iconBg: 'bg-violet-500',
    gradient: 'from-violet-500 to-violet-600'
  },
  pvp: { 
    bgLight: 'bg-red-50', 
    bgDark: 'bg-red-100', 
    text: 'text-red-600',
    iconBg: 'bg-red-500',
    gradient: 'from-red-500 to-red-600'
  },
  community: { 
    bgLight: 'bg-green-50', 
    bgDark: 'bg-green-100', 
    text: 'text-green-600',
    iconBg: 'bg-green-500',
    gradient: 'from-green-500 to-green-600'
  },
  exchange: { 
    bgLight: 'bg-amber-50', 
    bgDark: 'bg-amber-100', 
    text: 'text-amber-600',
    iconBg: 'bg-amber-500',
    gradient: 'from-amber-500 to-amber-600'
  },
  stats: { 
    bgLight: 'bg-teal-50', 
    bgDark: 'bg-teal-100', 
    text: 'text-teal-600',
    iconBg: 'bg-teal-500',
    gradient: 'from-teal-500 to-teal-600'
  }
};

// CSS pour les animations
const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export function Features() {
  const { t } = useTranslation();
  const { getPathForLang } = useI18n();

  const features = [
    {
      title: t('landing.features.feature1_title'),
      description: t('landing.features.feature1_description'),
      icon: <Smartphone className="w-8 h-8 text-white" aria-label={t('landing.features.feature1_title')} />,
      theme: featureThemes.collection,
      highlight: t('landing.features.feature1_tag')
    },
    {
      title: t('landing.features.feature2_title'),
      description: t('landing.features.feature2_description'),
      icon: <Library className="w-8 h-8 text-white" aria-label={t('landing.features.feature2_title')} />,
      theme: featureThemes.deck,
      highlight: t('landing.features.feature2_tag')
    },
    {
      title: t('landing.features.feature3_title'),
      description: t('landing.features.feature3_description'),
      icon: <Swords className="w-8 h-8 text-white" aria-label={t('landing.features.feature3_title')} />,
      theme: featureThemes.pvp,
      highlight: t('landing.features.feature3_tag')
    },
    {
      title: t('landing.features.feature4_title'),
      description: t('landing.features.feature4_description'),
      icon: <Users className="w-8 h-8 text-white" aria-label={t('landing.features.feature4_title')} />,
      theme: featureThemes.community,
      highlight: t('landing.features.feature4_tag')
    },
    {
      title: t('landing.features.feature5_title'),
      description: t('landing.features.feature5_description'),
      icon: <Shield className="w-8 h-8 text-white" aria-label={t('landing.features.feature5_title')} />,
      theme: featureThemes.exchange,
      highlight: t('landing.features.feature5_tag')
    },
    {
      title: t('landing.features.feature6_title'),
      description: t('landing.features.feature6_description'),
      icon: <BarChart className="w-8 h-8 text-white" aria-label={t('landing.features.feature6_title')} />,
      theme: featureThemes.stats,
      highlight: t('landing.features.feature6_tag')
    },
  ];

  // Ajouter les styles d'animation au DOM au moment du rendu
  React.useEffect(() => {
    // Vérifier si le style existe déjà
    const styleId = 'feature-animations';
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.innerHTML = animationStyles;
      document.head.appendChild(styleElement);
    }
    
    // Nettoyage lors du démontage du composant
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);
  
  return (
    <section className="py-16 sm:py-24 relative overflow-hidden bg-gradient-to-b from-white to-gray-50">
      {/* Éléments décoratifs */}
      <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-white to-transparent z-10 pointer-events-none"></div>
      <div className="absolute -top-40 left-0 right-0 h-80 bg-gradient-to-b from-blue-50/30 to-transparent transform -skew-y-3"></div>
      <div className="absolute -bottom-20 right-0 w-72 h-72 bg-gradient-to-tr from-blue-100/20 to-purple-100/20 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 left-0 w-72 h-72 bg-gradient-to-tr from-green-100/20 to-blue-100/20 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="text-center mb-16">
          <span className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full mb-3 inline-block">{t('landing.features.main_title')}</span>
          <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
            {t('landing.features.main_subtitle')}
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-600">
            {t('landing.features.main_description')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <div 
              key={feature.title} 
              className="relative bg-white rounded-xl shadow-md overflow-hidden group hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              style={{ 
                animationDelay: `${index * 0.1}s`,
                animation: 'fadeIn 0.8s ease-out forwards',
                opacity: 0
              }}
            >
              {/* Bandeau supérieur avec dégradé */}
              <div className={`h-2 w-full bg-gradient-to-r ${feature.theme.gradient}`}></div>
              
              {/* Badge de mise en avant */}
              <div className="absolute top-3 right-3">
                <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-md ${feature.theme.bgDark} ${feature.theme.text}`}>
                  {feature.highlight}
                </span>
              </div>
              
              <div className="p-6">
                {/* Icône dans un cercle avec couleur thématique */}
                <div className={`inline-flex items-center justify-center p-3 rounded-lg ${feature.theme.iconBg} mb-5 shadow-sm`}>
                  {feature.icon}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                
                {/* Indicateur visuel au survol */}
                <div className={`mt-4 h-0.5 w-0 ${feature.theme.bgDark} group-hover:w-full transition-all duration-300`}></div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Appel à l'action */}
        <div className="text-center mt-16">
          <Link 
            to={getPathForLang("/register")} 
            className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
          >
            {t('landing.features.view_all')}
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}